import { State } from '@progress/kendo-data-query';
import { filter, groupBy, some, sumBy } from 'lodash';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CanUsesInitialBudgets } from 'src/components/guards/PermissionsList';
import useAuth from 'src/hooks/useAuth';
import usePermission from 'src/hooks/usePermissions';
import useActionRequiredInvoicesApi, {
  IActionRequiredInvoiceApiProps,
  IActionRequiredResponse,
  IGlSummary,
  IItemsActionRequired,
} from 'src/services/useActionRequiredInvoicesApi';
import useGLPeriodApi, { GlPeriodObject } from 'src/services/useGLPeriodApi';
import { SetState } from 'src/types/generic';
import { fToMMDDYY } from 'src/utils/formatTime';
import { formatActionRequired } from 'src/utils/transformation';

interface ActionRequiredFilterType {
  value: number;
  label: string;
}

interface ActionRequiredInvoicesType {
  actionRequiredSelectionFilter: ActionRequiredFilterType;
  setActionRequiredSelectionFilter: SetState<ActionRequiredFilterType>;
  actionRequiredDataState: State;
  setActionRequiredDataState: SetState<State>;
  glSummaryDataTable: IGlSummary[];
  totalActionRequiredInvoices: number;
  formatedActionRequiredItems: IItemsActionRequired[];
  getActionRequiredInvoice: (actionRequiredInvoiceParams: IActionRequiredInvoiceApiProps) => void;
  setActionRequiredInvoice: SetState<IActionRequiredResponse>;
  actionRequiredLoading: boolean;
  setAdditionalGLCodeFilter: SetState<string>;
  additionalGLCodeFilter: string;
  selectedGLSummaryRowId: string;
  setSelectedGLSummaryRowId: SetState<string>;
  selectedInvoices: IItemsActionRequired[];
  setSelectedInvoices: SetState<IItemsActionRequired[]>;
  glPeriods: GlPeriodObject[];
  actionRequiredGridRefresh: () => Promise<IActionRequiredResponse>;
  actionRequiredGLSummaries: IGlSummary[];
  selectedGLSummary: IGlSummary | undefined;
  setSelectedGLSummary: Dispatch<SetStateAction<IGlSummary | undefined>>;

  fiscalYearStartDate: string | null;
  fiscalYearEastEndDate: string | null;
  usesBudgets: boolean;
}

export const ActionRequiredInvoicesContext = createContext({} as ActionRequiredInvoicesType);

interface ActionRequiredInvoicesContextProps {
  children: ReactNode;
}

const actionRequiredSelectionFilterInitialState = {
  value: 0,
  label: 'Show All',
};

export const initialActionRequiredDataState: State = {
  skip: 0,
  take: 50,
  sort: [{ field: 'invoiceDate', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

export const filterObjectsByGLCode = (
  data: IItemsActionRequired[],
  searchValue: string
): IItemsActionRequired[] =>
  filter(data, (item: IItemsActionRequired) =>
    some(item?.glSummaries || [], (glSummary: IGlSummary) => glSummary?.glCd === searchValue)
  );

const ActionRequiredInvoicesContextProvider: FC<ActionRequiredInvoicesContextProps> = ({
  children,
}) => {
  const [actionRequiredSelectionFilter, setActionRequiredSelectionFilter] =
    useState<ActionRequiredFilterType>(actionRequiredSelectionFilterInitialState);
  const [additionalGLCodeFilter, setAdditionalGLCodeFilter] = useState<string>('');
  const [selectedGLSummaryRowId, setSelectedGLSummaryRowId] = useState<string>('');
  const [actionRequiredDataState, setActionRequiredDataState] = useState<State>({
    ...initialActionRequiredDataState,
  });
  const {
    totalActionRequiredInvoices,
    actionRequiredItems,
    getActionRequiredInvoice,
    setActionRequiredInvoice,
    actionRequiredLoading,
    actionRequiredGLSummaries,
  } = useActionRequiredInvoicesApi();

  const { user } = useAuth();
  const usesBudgets = usePermission({ permissions: CanUsesInitialBudgets });

  const [selectedGLSummary, setSelectedGLSummary] = useState<IGlSummary>();

  const glCodeFormatedActionRequired = useMemo(
    () =>
      (!!additionalGLCodeFilter &&
        filterObjectsByGLCode(actionRequiredItems, additionalGLCodeFilter)) ||
      actionRequiredItems,
    [actionRequiredItems, additionalGLCodeFilter]
  );

  const formatedActionRequiredItems = useMemo(
    () => formatActionRequired(glCodeFormatedActionRequired),
    [glCodeFormatedActionRequired]
  );

  const [selectedInvoices, setSelectedInvoices] = useState<IItemsActionRequired[]>([]);
  const companyId = user.CompanyId;
  const { glPeriods, getGLPeriods } = useGLPeriodApi(String(companyId));
  const { fiscalYearStartDate, fiscalYearEastEndDate } = useMemo(() => {
    const fiscalYearsArray = glPeriods
      ?.filter((glSummary) => glSummary?.fiscalYear === selectedGLSummary?.fiscalYear)
      ?.slice()
      ?.sort((a, b) => a?.startDate.localeCompare(b?.startDate));

    let fiscalYearStartDate = null;
    let fiscalYearEastEndDate = null;

    if (fiscalYearsArray && fiscalYearsArray?.length > 0) {
      fiscalYearStartDate = fiscalYearsArray[0].startDate;
      fiscalYearEastEndDate = fiscalYearsArray[fiscalYearsArray.length - 1].endDate;
    }

    return {
      fiscalYearStartDate,
      fiscalYearEastEndDate,
    };
  }, [glPeriods, selectedGLSummary?.fiscalYear]);
  const glSummaryDataTable: IGlSummary[] = useMemo(() => {
    const glSummaries = formatedActionRequiredItems?.reduce(
      (acc, obj) => acc.concat(obj?.glSummaries),
      [] as IGlSummary[]
    );
    const glSummariesGrouped = groupBy(
      glSummaries,
      (item) => item.glCd + '-' + item.budgetPeriodStartDate + '-' + item.glBalance
    );
    let glSummariesTable: IGlSummary[] = [];
    for (const group in glSummariesGrouped) {
      const glCodesArray = glSummariesGrouped[group];
      glSummariesTable.push({
        ...glCodesArray?.[0],
        rowId: group,
        poGlTotal: sumBy(glCodesArray, 'poGlTotal'),
      });
    }
    return glSummariesTable.map((summary) => ({
      ...summary,
      accountingPeriod: `${fToMMDDYY(summary.budgetPeriodStartDate)}-${fToMMDDYY(
        summary.budgetPeriodEndDate
      )}`,
    }));
  }, [formatedActionRequiredItems]);

  const actionRequiredGridRefresh = useCallback(
    () => getActionRequiredInvoice({ actionRequiredDataState, actionRequiredSelectionFilter }),
    [actionRequiredDataState, actionRequiredSelectionFilter, getActionRequiredInvoice]
  );

  useEffect(() => {
    actionRequiredGridRefresh();
  }, [actionRequiredGridRefresh]);

  useEffect(() => {
    if (companyId) getGLPeriods();
  }, [getGLPeriods, companyId]);

  return (
    <ActionRequiredInvoicesContext.Provider
      value={{
        actionRequiredSelectionFilter,
        setActionRequiredSelectionFilter,
        actionRequiredDataState,
        setActionRequiredDataState,
        glSummaryDataTable,
        totalActionRequiredInvoices,
        formatedActionRequiredItems,
        getActionRequiredInvoice,
        setActionRequiredInvoice,
        actionRequiredLoading,
        setAdditionalGLCodeFilter,
        additionalGLCodeFilter,
        selectedGLSummaryRowId,
        setSelectedGLSummaryRowId,
        selectedInvoices,
        setSelectedInvoices,
        actionRequiredGridRefresh,
        glPeriods,
        actionRequiredGLSummaries,
        fiscalYearStartDate,
        fiscalYearEastEndDate,
        selectedGLSummary,
        setSelectedGLSummary,
        usesBudgets,
      }}
    >
      {children}
    </ActionRequiredInvoicesContext.Provider>
  );
};

export default ActionRequiredInvoicesContextProvider;
